// Contract 11 §269 — the billing return is "idempotent". The in-memory promise map only shares a
// CONCURRENT run; it is deleted on settle, so a reload, a Back navigation or a bookmarked provider
// URL replayed the whole return: another account-context reload, another credit refresh and
// another "Payment complete" toast for a payment that already completed.
//
// This ledger records a SETTLED return so the replay can short-circuit. It is deliberately narrow:
//
//  • `sessionStorage`, not `localStorage` — the record is about this tab's journey, and a return
//    replayed in a new tab days later should re-verify against the server rather than trust a
//    stale local claim.
//  • only params whose value is a provider-issued unique token may be recorded. `stripe_session`
//    carries a Stripe checkout-session id; `package_downgraded` carries a boolean-ish flag, and
//    keying on `package_downgraded:1` would make a SECOND genuine downgrade in the same tab a
//    silent no-op. A weak key is worse than no key.

import type { BillingReturnParam } from './BillingReturnFlow'

const STORAGE_KEY = 'orphex.billing.return.settled.v1'

/** Params whose query value uniquely identifies ONE provider return. */
const IDEMPOTENT_PARAMS = new Set<BillingReturnParam>(['stripe_session'])

/** Keep the ledger bounded; a tab never legitimately completes many checkouts. */
const MAX_ENTRIES = 20

export type SettledStatus = 'confirmed' | 'failed'

export function isIdempotencyCapable(param: BillingReturnParam, value: string): boolean {
  return IDEMPOTENT_PARAMS.has(param) && value.trim().length > 0
}

function read(): Record<string, SettledStatus> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, SettledStatus>)
      : {}
  } catch {
    // A storage-disabled browser degrades to the pre-ledger behaviour: correct, just not deduped.
    return {}
  }
}

function write(entries: Record<string, SettledStatus>): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries))
  } catch {
    // Quota/privacy mode — never fail a payment return over a bookkeeping write.
  }
}

export function readSettled(key: string): SettledStatus | null {
  return read()[key] ?? null
}

export function recordSettled(key: string, status: SettledStatus): void {
  const entries = read()
  entries[key] = status
  const keys = Object.keys(entries)
  if (keys.length > MAX_ENTRIES) {
    for (const stale of keys.slice(0, keys.length - MAX_ENTRIES)) delete entries[stale]
  }
  write(entries)
}

/** Test isolation only. */
export function clearSettledReturns(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}
